<script setup lang="ts">
import {
  BarElement,
  Chart as ChartJS,
  Legend,
  Tooltip,
  CategoryScale,
  LinearScale,
  type ActiveElement,
  type ChartEvent,
  type ChartData,
  type ChartOptions,
} from 'chart.js'
import { computed } from 'vue'
import { Bar } from 'vue-chartjs'
import {
  baseBarchartDatasetSettings,
  normalizeDataset,
  retrieveClickedChartDatum,
  tooltipStatsLabelFor,
  getCSSVariableColor,
  type BarchartDataset,
  type ChartClickEventDatum,
  type PartsStatusChartConf,
} from '@/components/rc/work-order-dashboard/charts-helpers'
import type { Matrix } from '@/types/shared/common'
import type { MinerPartStatusStatistics, PartStatusStatistics } from '@/types/rc/work-orders'
import { useDark } from '@vueuse/core'

const props = defineProps<{ stats: MinerPartStatusStatistics }>()
const emit = defineEmits<{ (event: 'barClick', value: ChartClickEventDatum): void }>()

const isDark = useDark()

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend)

/** One item per bar stack */
const labels: PartsStatusChartConf['label'][] = ['PSU', 'CB', 'Fans', 'HB']

interface PartsBarchartDataset extends BarchartDataset {
  label: PartsStatusChartConf['category']
}

/** One item per dataset */
const partsBarchartSettings: Omit<PartsBarchartDataset, 'data'>[] = [
  { backgroundColor: getCSSVariableColor('--color-sprout-400'), label: 'Good' },
  { backgroundColor: getCSSVariableColor('--color-sprout-600'), label: 'Repaired/Replaced' },
  { backgroundColor: getCSSVariableColor('--color-orange-500'), label: 'Fail' },
  { backgroundColor: getCSSVariableColor('--color-xanthous-300'), label: 'Blocked' },
  { backgroundColor: getCSSVariableColor('--color-red-700'), label: 'Repair not authorized' },
  { backgroundColor: getCSSVariableColor('--color-red-900'), label: 'Unrepairable/Irreparable' },
  {
    backgroundColor: isDark.value
      ? getComputedStyle(document.documentElement).getPropertyValue('--component-fill-badge')
      : '#e2e2e2',
    label: 'Undiagnosed',
  },
]

type Matrix7x4 = Matrix<7, 4>

const statisticToArray = (stat: PartStatusStatistics): [number, number, number, number] => [
  stat.psus,
  stat.cbs,
  stat.fans,
  stat.hbs,
]

/** labels# x datasets# matrix */
const sourceData = computed<Matrix7x4>(() => [
  statisticToArray(props.stats.good),
  statisticToArray(props.stats.repaired_or_replaced),
  statisticToArray(props.stats.fail),
  statisticToArray(props.stats.blocked),
  statisticToArray(props.stats.repair_not_authorized),
  statisticToArray(props.stats.unrepairable_or_irrepairable),
  statisticToArray(props.stats.undiagnosed),
])

/** Base dataset. We'll lookup tooltip-displayed data here. */
const baseData = computed<BarchartDataset[]>(() =>
  sourceData.value.map((data, i) => ({
    ...partsBarchartSettings[i],
    ...baseBarchartDatasetSettings.value,
    data,
  }))
)

/** Full-blown chartData. Labels + zipped out datasets. */
const chartData = computed<ChartData<'bar', number[], string>>(() => ({
  labels,
  datasets: normalizeDataset(baseData.value),
}))

// Emit 'barClick' so parent can filter out the table
const onClick = (event: ChartEvent, elements: ActiveElement[]) =>
  emit('barClick', retrieveClickedChartDatum(chartData, elements))

const chartOptions = computed<ChartOptions<'bar'>>(() => ({
  responsive: true,
  indexAxis: 'y',
  onClick,
  scales: {
    x: { stacked: true, grid: { display: false }, min: 0, max: 100, display: false },
    y: {
      stacked: true,
      grid: { display: false },
      ticks: { align: 'start', crossAlign: 'far' },
      border: { display: false },
    },
  },
  plugins: {
    tooltip: {
      enabled: true,
      callbacks: {
        label: tooltipStatsLabelFor(baseData),
      },
    },
    legend: {
      position: 'bottom',
      labels: {
        usePointStyle: true,
      },
    },
  },
}))
</script>

<template>
  <div class="w-full h-[400px] flex items-center justify-center">
    <Bar :data="chartData" :options="chartOptions" />
  </div>
</template>
